// [목적] 인증 상태를 전역 React Context 로 노출 — status/user/isOnboarded/refetchMe
// [이유] fe-be-wire-up Wave 1 WC-FE-AUTH-01 + oauth-login Wave 5 WC-FE-PROVIDER-01 흡수.
//         AuthProvider 가 staleTime=∞ 로 GET /auth/me 를 캐싱하고
//         ProtectedRoute/GuestOnlyRoute/useAuth 가 이 컨텍스트를 소비한다.
//         LGO-FE-02(auth-logout-hardening Wave 3) — 'gangwon:logout' reason payload 수신:
//           user_initiated → 안내 억제, session_expired → auth.SESSION_EXPIRED Snackbar,
//           force_revoked → auth.SESSION_REVOKED Snackbar. returnTo 보존.
//         2026-09-18(L-7) — session_expired/force_revoked 경로에서 clearUserCaches() 호출 추가 —
//           세션 만료·강제 로그아웃 경로에도 SW 폴리오 캐시(-folio) 정리를 보장(user_initiated 는
//           useAuth.logout() 이 dispatch 전 이미 호출하므로 중복 방지를 위해 여기서는 생략).
//         root-route-guest-onboarding-routing(2026-06-04) — wasAuthenticatedRef(latch) 도입:
//           "한 번이라도 authenticated 였던 적" 을 latch ref 로 추적하여
//           첫 게스트(한 번도 인증 없음)의 오탐 SESSION_EXPIRED redirect/Snackbar 를 제거.
//           실제 만료(wasAuthenticatedRef=true) 사용자는 기존대로 /login?error=SESSION_EXPIRED 안내.
// [사이드 이팩트] 401 또는 ApiError('auth.*UNAUTHORIZED*') → status='guest' (silent fail — 에러 UI 없음).
//                 로그인 성공 직후 첫 hydrate 시 clearAnonSessionId() 1회 호출 (AC-10/Q-5).
//                 'gangwon:logout' CustomEvent 구독 → reason 에 따라 /login?returnTo=...&error=... 라우팅.
//                 user_initiated reason 은 안내 억제(useAuth.logout() 이 별도로 /login 이동 처리).
//                 wasAuthenticatedRef=false(첫 게스트) 시 setSessionEnded+removeQueries 실행 후 redirect/Snackbar 생략
//                   → status='guest' 확정 → RootSwitch 가 LandingPage 렌더 (AC-1/AC-2 보장).
//                 wasAuthenticatedRef 는 풀 리로드 시 false 초기화 — 이미 /login redirect 완료 후라 UX 영향 없음.
// [수정 시 고려사항] meOutputSchema / onboardedAt 필드명 변경 시 이 파일 및 auth.client.ts 동기 갱신.
//                   staleTime=Infinity + gcTime=Infinity 정책 — 앱 생명주기 동안 캐시 유지.
//                   AuthProvider 는 QueryClientProvider 하위, RouterProvider 상위에 위치해야 한다.
//                   RouterProvider 상위 배치 시 useNavigate 미사용 — window.location.href 로 라우팅(React Router 컨텍스트 외부).
//                   latch 가드 삽입 위치: setSessionEnded(L142)/removeQueries(L143) 뒤, user_initiated return(L146) 뒤,
//                     errorCode 계산(L149) 앞 → R-1(sessionEnded 무조건)/R-2(/login early-return) 동시 보존.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { me as fetchMe, type MeOutput } from '../api/auth.client.js';
import { clearAnonSessionId } from '../api/anonSession.js';
import { queryKeys } from '../api/queryKeys.js';
import { ApiError, type LogoutEventDetail } from '../api/http.js';
import { sanitizeNextPath } from '../api/safeRedirect.js';
import { clearUserCaches } from '../utils/clearUserCaches.js';

// ─── 컨텍스트 타입 ───────────────────────────────────────────────────────────

export type AuthStatus = 'loading' | 'authenticated' | 'guest';

export interface AuthContextValue {
  /** 인증 상태 */
  status: AuthStatus;
  /** 인증된 사용자 정보 (loading/guest 시 null) */
  user: MeOutput | null;
  /** 온보딩 완료 여부 (onboardedAt !== null) */
  isOnboarded: boolean;
  /** /auth/me 를 강제 재조회 */
  refetchMe: () => Promise<void>;
}

// ─── 컨텍스트 ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * [목적] AuthProvider — 인증 컨텍스트 공급자
 * [이유] WC-FE-AUTH-01: staleTime=∞ useQuery 로 GET /auth/me 캐싱 + context 노출
 * [사이드 이팩트] 401 → status='guest'. 성공 첫 hydrate 시 clearAnonSessionId() 호출.
 * [수정 시 고려사항] children 은 loading 중에도 항상 렌더 — ProtectedRoute 가 분기 처리.
 */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const queryClient = useQueryClient();

  // [목적] 세션 종료(refresh 실패=guest 또는 mid-session 만료) 플래그 — /auth/me 쿼리를 비활성화해 무한 루프 차단
  // [이유] 2026-05-30 white-screen 버그: 무효 gw_at + gw_rt 없음 상태에서 /auth/me 401 → http.ts 자동 refresh →
  //         /auth/refresh 401(REFRESH_TOKEN_MISSING) → 'gangwon:logout' 디스패치 → 기존 핸들러의 removeQueries 가
  //         (TanStack Query v5에선) 활성 observer 의 refetch 를 트리거 → /auth/me 재요청 → 무한 루프.
  //         루프 동안 isFetching=true 고정 → status='loading' → RootSwitch 가 빈 화면(white screen) 렌더.
  //         기존 주석(removeQueries는 refetch 미트리거)의 가정이 v5에서 틀려 발생.
  // [사이드 이팩트] sessionEnded=true 이면 useQuery enabled=false → 재요청 차단 + status/user 를 guest 로 강제.
  //                 재로그인(refetchMe) 시 false 로 복원 후 재요청.
  // [수정 시 고려사항] removeQueries 의존 제거 — 루프 원천 차단. stale authed data 는 sessionEnded 가 user/status 에서 마스킹.
  const [sessionEnded, setSessionEnded] = useState(false);

  // ── /auth/me 쿼리 ────────────────────────────────────────────────────────
  const { data, isFetching, refetch } = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchMe,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    // 401 같은 에러는 silent fail — throwOnError 없이 data=undefined
    throwOnError: false,
    // sessionEnded(logout) 시 비활성 — refresh 실패 후 재요청 루프 차단 (2026-05-30 white-screen fix)
    enabled: !sessionEnded,
  });

  // ── anon hydrate-then-discard (AUTH-08 / AC-10) ──────────────────────────
  // 이전 상태 추적 — 처음으로 authenticated 가 되는 순간 1회만 clearAnonSessionId 호출
  const prevStatusRef = useRef<AuthStatus>('loading');

  // ── 첫 게스트 오탐 제거 (root-route-guest-onboarding-routing 2026-06-04) ──
  // [목적] "한 번이라도 authenticated 였던 적"을 latch 로 추적 — 첫 게스트(false)와 실제 만료(true) 구분
  // [이유] prevStatusRef 는 매 status 변화마다 갱신되어 "loading→guest"(첫 게스트)와
  //         "authenticated→guest"(실제 만료) 를 구분 불가 → 별도 latch ref(한 번 true → 영구) 필수.
  //         첫 게스트에게 SESSION_EXPIRED redirect/Snackbar 는 false positive — setSessionEnded+removeQueries 만 수행.
  // [사이드 이팩트] ref 이므로 StrictMode dev 2회 effect 에도 set 멱등. 풀 리로드 시 false 초기화(무해).
  // [수정 시 고려사항] set 위치는 L112-117 useEffect(authenticated 감지) 본문 — prevStatusRef 동일 트리거 재사용.
  //                   latch 가드는 handleLogout 의 user_initiated return(현 L146) 직후, errorCode(현 L149) 직전.
  const wasAuthenticatedRef = useRef(false);

  // ── 상태 계산 ─────────────────────────────────────────────────────────────
  // sessionEnded(logout) 시 stale authed data 와 무관하게 guest 강제 (루프 차단 + 일관성)
  let status: AuthStatus;
  if (sessionEnded) {
    status = 'guest';
  } else if (isFetching && data === undefined) {
    status = 'loading';
  } else if (data !== undefined) {
    status = 'authenticated';
  } else {
    status = 'guest';
  }

  const isOnboarded = !sessionEnded && data !== undefined && data.onboardedAt !== null;

  // AUTH-08: guest/loading → authenticated 전환 시 anon ID 폐기 (1회)
  // latch: 첫 authenticated 도달 시 wasAuthenticatedRef.current = true (한 번 true → 영구)
  useEffect(() => {
    if (status === 'authenticated' && prevStatusRef.current !== 'authenticated') {
      clearAnonSessionId();
    }
    if (status === 'authenticated') {
      wasAuthenticatedRef.current = true;
    }
    prevStatusRef.current = status;
  }, [status]);

  // ── 'gangwon:logout' 이벤트 구독 ─────────────────────────────────────────
  // [목적] http.ts doRefresh 실패 시 디스패치되는 logout 이벤트 처리 — auth 캐시 제거로 status='guest' 전환
  // [이유] 2026-05-16 reauth-fix — invalidateQueries 는 stale 표시 + 자동 refetch 트리거 → /auth/me 재호출 →
  //         또 401 → doRefresh → /auth/refresh 401 → logout event → invalidate → ... 무한 루프 발생.
  //         BE RateLimitGuard 가 60/60s 초과 시 429 차단해도 FE 가 계속 호출 시도 — RateLimit 폭주.
  //         LGO-FE-02(auth-logout-hardening Wave 3) — reason payload 수신하여 사유별 분기:
  //           user_initiated → 안내 억제(useAuth.logout() 이 /login 이동 처리). auth cache 정리만.
  //           session_expired → returnTo 보존 + /login?returnTo=...&error=auth.SESSION_EXPIRED 이동.
  //           force_revoked  → returnTo 보존 + /login?returnTo=...&error=auth.SESSION_REVOKED 이동.
  // [사이드 이팩트] removeQueries 는 cache 에서 즉시 제거하며 refetch 미트리거 — status='guest' 로 1회만 전환.
  //                 그 후 사용자가 다시 로그인하면 useQuery 가 새 mount 로 fetchMe 호출 — 정상 흐름 복원.
  //                 user_initiated 는 안내 없이 sessionEnded=true 만 설정 — useAuth.logout() 이 /login 이동.
  //                 자동 로그아웃(session_expired/force_revoked) 은 window.location.href 로 /login 이동.
  //                   AuthProvider 는 RouterProvider 상위에 위치하므로 useNavigate 사용 불가 → window.location.href.
  // [수정 시 고려사항] invalidate 로 되돌리면 무한 루프 재현. 다른 'auth' prefix 쿼리 추가 시 본 removeQueries 영향 검토.
  //                   returnTo 경로는 이벤트 수신 시점의 window.location 기준 — SPA 라우터 state 와 동기화 불필요.
  useEffect(() => {
    function handleLogout(e: Event): void {
      // LGO-FE-02: reason payload 추출 (CustomEvent<LogoutEventDetail>)
      const reason = (e as CustomEvent<LogoutEventDetail>).detail?.reason ?? 'session_expired';

      // [2026-05-30 white-screen fix] removeQueries(refetch 트리거 → 루프) 대신 sessionEnded 플래그로
      //   /auth/me 쿼리를 비활성화 → 재요청 차단 + status=guest. stale authed data 는 user 마스킹으로 정리.
      setSessionEnded(true);
      queryClient.removeQueries({ queryKey: ['auth'] });

      // LGO-FE-02: user_initiated 는 useAuth.logout() 이 /login 이동을 직접 처리 — 여기서는 안내 억제.
      // [2026-09-18] L-7 조치 — user_initiated 는 useAuth.logout() 이 dispatch 전에 이미 clearUserCaches()
      // 를 직접 호출했으므로 여기서 또 호출하면 중복 실행된다(테스트로 발견 — cacheDelete 2회 호출).
      // 세션 만료(session_expired)·강제 로그아웃(force_revoked) 경로만 여기서 정리(기존엔 이 두 경로에
      // SW 폴리오 캐시 정리가 전혀 없었음 — useAuth.logout 버튼 경로에만 존재했던 문제).
      if (reason === 'user_initiated') return;
      void clearUserCaches();

      // [첫 게스트 오탐 제거] 한 번도 authenticated 였던 적 없으면(=첫 게스트) SESSION_EXPIRED redirect/Snackbar 생략.
      // setSessionEnded(true)+removeQueries 는 이미 위에서 실행됨(R-1 유지) → guest 확정 → RootSwitch 가 LandingPage 렌더.
      if (!wasAuthenticatedRef.current) return;

      // LGO-FE-02: 자동 로그아웃 — returnTo 보존 + error 코드로 /login 이동
      const errorCode =
        reason === 'force_revoked' ? 'auth.SESSION_REVOKED' : 'auth.SESSION_EXPIRED';

      // [reload-loop fix 2026-06-01 v2]
      // 이미 /login 페이지에서 401 → refresh 401 → handleLogout 이 호출된 경우
      // window.location.href 로 또 /login 으로 이동하면 setSessionEnded(true) 가 휘발되어
      // 마운트 직후 /auth/me 가 다시 401 → refresh 401 → reload 의 무한 루프가 발생한다.
      // → 이미 /login 이면 navigation/URL 변경 자체를 생략. sessionEnded=true 만으로 useQuery 재요청이
      //    차단되므로 LoginPage 가 guest 상태로 정상 렌더된다.
      // 주의: window.history.replaceState 로 ?error=... 를 붙이면 React Router 의 useSearchParams 에
      //       변경이 전파되지 않아 LoginPage 의 error-clear useEffect 가 실행되지 않고 URL 에 잔존한다.
      //       비로그인 사용자가 /login 에서 401 받는 건 정상 흐름이므로 안내 자체가 불필요.
      //       실제 사용자 액션(로그아웃 직후 만료 안내) 은 보호 라우트에서의 풀 리로드 분기가 담당한다.
      if (window.location.pathname === '/login') {
        return;
      }

      const returnTo = encodeURIComponent(
        sanitizeNextPath(window.location.pathname + window.location.search),
      );
      window.location.href = `/login?returnTo=${returnTo}&error=${encodeURIComponent(errorCode)}`;
    }
    window.addEventListener('gangwon:logout', handleLogout);
    return () => {
      window.removeEventListener('gangwon:logout', handleLogout);
    };
  }, [queryClient]);

  // ── refetchMe ─────────────────────────────────────────────────────────────
  // 재로그인 등으로 다시 인증 확인이 필요할 때 — sessionEnded 해제 후 /auth/me 재요청
  const refetchMe = useCallback(async (): Promise<void> => {
    setSessionEnded(false);
    try {
      await refetch();
    } catch (err) {
      // ApiError 401 — silent fail (status='guest' 로 전환됨)
      if (!(err instanceof ApiError)) {
        // 예기치 않은 에러는 로그만
        console.warn('[AuthProvider] refetchMe unexpected error', err);
      }
    }
  }, [refetch]);

  const value: AuthContextValue = {
    status,
    // sessionEnded 시 stale authed data 를 노출하지 않도록 null 마스킹 (status=guest 와 일관)
    user: sessionEnded ? null : (data ?? null),
    isOnboarded,
    refetchMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── 훅 ─────────────────────────────────────────────────────────────────────

/**
 * [목적] AuthContext 소비 훅 — AuthProvider 외부에서 호출 시 에러
 * [이유] 컨텍스트 미주입 상태의 런타임 에러를 조기에 노출
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] AuthProvider 밖에서 호출되면 throw — Provider 위치 확인
 */
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within <AuthProvider>');
  }
  return ctx;
}
